import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Search, Trash2, Package } from "lucide-react";
import { useNotification } from "../../hooks/useNotification";
import {
  getOutletStockAvailability,
  requestStock,
} from "../../services/api";
import "./StockRequest.css";

const MAX_ITEMS = 30;

const buildEmptyItem = () => ({
  id: Date.now() + Math.random(),
  quantity: 1,
  selectedProductId: "",
  productSearch: "",
  showDropdown: false,
  productMeta: null,
});

const getVariantLabel = (variant) => {
  if (!variant) return "";
  return (
    variant.product_model_code?.trim() ||
    variant.product_model_description?.trim() ||
    variant.product_description?.trim() ||
    ""
  );
};

function StockRequestForm({ activeCheckIn, onCancel, onSave, prefill = null }) {
  const { t } = useTranslation();
  const { showSuccess, showWarning, showError } = useNotification();

  const outletId = activeCheckIn?.outlet_id;

  const [items, setItems] = useState(() => {
    // Resubmitting a rejected/cancelled request: seed the lines, then hydrate
    // the warehouse stock figure below so it reflects current stock.
    if (Array.isArray(prefill?.items) && prefill.items.length > 0) {
      return prefill.items.map((item) => ({
        ...buildEmptyItem(),
        id: Date.now() + Math.random(),
        selectedProductId: item.product_id,
        productSearch: item.model_name || "",
        quantity: item.quantity,
      }));
    }
    return [buildEmptyItem()];
  });
  const [notes, setNotes] = useState(prefill?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [warehouse, setWarehouse] = useState(null);

  const [optionsByItem, setOptionsByItem] = useState({});
  const [loadingByItem, setLoadingByItem] = useState({});
  const searchTimeoutsRef = useRef({});
  const latestSearchRef = useRef({});

  /**
   * Load products stocked at this outlet's warehouse. available_stock on each
   * row is informational only — it is shown to the promoter but does not cap
   * how much they can request.
   */
  const fetchOptions = useCallback(
    async (itemId, query = "") => {
      if (!outletId) return;

      const normalizedQuery = String(query || "").trim();
      latestSearchRef.current[itemId] = normalizedQuery;
      setLoadingByItem((prev) => ({ ...prev, [itemId]: true }));

      try {
        const response = await getOutletStockAvailability(outletId, {
          q: normalizedQuery,
          limit: 30,
        });

        if (latestSearchRef.current[itemId] !== normalizedQuery) return;

        if (response?.warehouse) setWarehouse(response.warehouse);
        setOptionsByItem((prev) => ({
          ...prev,
          [itemId]: Array.isArray(response?.data) ? response.data : [],
        }));
      } catch (error) {
        console.error("Failed to load stock availability:", error);
        if (latestSearchRef.current[itemId] === normalizedQuery) {
          setOptionsByItem((prev) => ({ ...prev, [itemId]: [] }));
        }
      } finally {
        if (latestSearchRef.current[itemId] === normalizedQuery) {
          setLoadingByItem((prev) => ({ ...prev, [itemId]: false }));
        }
      }
    },
    [outletId],
  );

  const queueFetch = useCallback(
    (itemId, query = "") => {
      if (searchTimeoutsRef.current[itemId]) {
        clearTimeout(searchTimeoutsRef.current[itemId]);
      }
      searchTimeoutsRef.current[itemId] = setTimeout(() => {
        fetchOptions(itemId, query);
      }, 250);
    },
    [fetchOptions],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".searchable-dropdown")) {
        setItems((prev) =>
          prev.map((item) => ({ ...item, showDropdown: false })),
        );
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const currentTimeouts = searchTimeoutsRef.current;
    return () => {
      Object.values(currentTimeouts).forEach((id) => clearTimeout(id));
    };
  }, []);

  /**
   * Attach the current warehouse stock figure to prefilled lines, so a
   * resubmitted request shows today's stock next to the old quantities.
   */
  useEffect(() => {
    const prefillItems = prefill?.items;
    if (!outletId || !Array.isArray(prefillItems) || prefillItems.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const lookups = await Promise.all(
          prefillItems.map((item) =>
            getOutletStockAvailability(outletId, {
              q: item.model_name || "",
              limit: 30,
            }).catch(() => null),
          ),
        );

        if (cancelled) return;

        const foundByProduct = new Map();
        lookups.forEach((response, index) => {
          if (response?.warehouse) setWarehouse(response.warehouse);
          const targetId = prefillItems[index]?.product_id;
          const match = (response?.data || []).find(
            (row) => Number(row.product_id) === Number(targetId),
          );
          if (match) foundByProduct.set(Number(targetId), match);
        });

        setItems((prev) =>
          prev.map((item) => {
            const match = foundByProduct.get(Number(item.selectedProductId));
            if (!match) return item;
            return {
              ...item,
              productSearch: getVariantLabel(match) || item.productSearch,
              productMeta: {
                productName: match.product_description || "",
                modelName: getVariantLabel(match),
                modelDescription: match.product_model_description || "",
                categoryName:
                  match.productGroup?.product_group_description || "",
                colour: match.colour || "",
                availableStock: match.available_stock,
              },
            };
          }),
        );
      } catch (error) {
        console.error("Failed to hydrate resubmitted request:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [outletId, prefill]);

  const updateItem = (index, patch) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleSearchChange = (index, value) => {
    updateItem(index, {
      productSearch: value,
      showDropdown: true,
      selectedProductId: "",
      productMeta: null,
    });
  };

  const handleSelect = (index, variant) => {
    updateItem(index, {
      selectedProductId: variant.product_id,
      productSearch: getVariantLabel(variant),
      showDropdown: false,
      productMeta: {
        productName: variant.product_description || "",
        modelName: getVariantLabel(variant),
        modelDescription: variant.product_model_description || "",
        categoryName: variant.productGroup?.product_group_description || "",
        colour: variant.colour || "",
        availableStock: variant.available_stock,
      },
    });
  };

  const addItem = () => {
    if (items.length < MAX_ITEMS) {
      setItems((prev) => [...prev, buildEmptyItem()]);
    }
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const invalidIndex = items.findIndex(
      (item) =>
        !Number.isInteger(Number.parseInt(item.selectedProductId, 10)) ||
        !String(item.productSearch || "").trim(),
    );
    if (invalidIndex !== -1) {
      showWarning(
        t("se.stockRequest.productValidationError", {
          n: invalidIndex + 1,
          defaultValue: `Please select a product for item ${invalidIndex + 1}.`,
        }),
      );
      return;
    }

    // Quantity must be a positive whole number. There is no upper cap — a
    // promoter may request more than the warehouse currently holds.
    const invalidQtyIndex = items.findIndex(
      (item) => !(Number.parseInt(item.quantity, 10) > 0),
    );
    if (invalidQtyIndex !== -1) {
      showWarning(
        t("se.stockRequest.quantityValidationError", {
          n: invalidQtyIndex + 1,
          defaultValue: `Please enter a valid quantity for item ${invalidQtyIndex + 1}.`,
        }),
      );
      return;
    }

    const payload = {
      outlet_id: outletId,
      check_in_id: activeCheckIn?.check_in_id ?? null,
      items: items.map((item) => ({
        product_id: Number.parseInt(item.selectedProductId, 10),
        quantity: Number.parseInt(item.quantity, 10) || 1,
      })),
      notes: notes.trim(),
      // Links a "Request again" submission back to the rejected/cancelled original.
      resubmitted_from_id: prefill?.sourceId ?? null,
    };

    try {
      setSubmitting(true);
      const result = await requestStock(payload);
      if (result?.success) {
        showSuccess(
          t("se.stockRequest.successSubmitted", {
            defaultValue: "Stock request submitted.",
          }),
        );
        onSave?.(result);
      } else {
        throw new Error("Request failed");
      }
    } catch (error) {
      const data = error?.response?.data;
      const message =
        data?.error ||
        error?.message ||
        t("se.stockRequest.submitError", {
          defaultValue: "Failed to submit stock request. Please try again.",
        });
      setFormError(message);
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sr-container sale-mobile-shell">
      <div className="se-sales-selected-visit">
        <div className="se-sales-selected-visit-top">
          <div>
            <div className="se-sales-selected-visit-label">
              {t("se.stockRequest.requestingFor", {
                defaultValue: "Requesting stock for",
              })}
            </div>
            <div className="se-sales-selected-visit-name">
              {activeCheckIn?.outlet_name ||
                t("se.stockRequest.currentOutlet", {
                  defaultValue: "Current outlet",
                })}
            </div>
          </div>
        </div>
        {activeCheckIn?.outlet_address && (
          <div className="se-sales-selected-visit-address">
            {activeCheckIn.outlet_address}
          </div>
        )}
        {warehouse && (
          <div className="se-sales-selected-visit-address sr-warehouse-line">
            {t("se.stockRequest.warehouseLabel", { defaultValue: "Warehouse" })}:{" "}
            <strong>
              {[warehouse.warehouse_code, warehouse.warehouse_name]
                .filter(Boolean)
                .join(" · ")}
            </strong>
          </div>
        )}
      </div>

      <form id="stock-request-form" onSubmit={handleSubmit} className="sale-form">
        {items.map((item, index) => {
          const options = optionsByItem[item.id] || [];
          const isLoading = Boolean(loadingByItem[item.id]);
          const meta = item.productMeta;

          return (
            <div
              key={item.id}
              className="sale-item-section sale-item-grid-section"
            >
              <div className="sale-item-section-header">
                <h4>
                  {t("se.stockRequest.itemTitle", {
                    n: index + 1,
                    defaultValue: `Item ${index + 1}`,
                  })}
                </h4>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="sale-item-remove-btn"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    {t("se.stockRequest.remove", { defaultValue: "Remove" })}
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>
                  {t("se.stockRequest.productLabel", {
                    defaultValue: "Product / Model",
                  })}
                  <span className="required-asterisk" aria-hidden="true">
                    *
                  </span>
                </label>
                <div className="searchable-dropdown">
                  <div className="searchable-dropdown-input">
                    <Search size={18} className="searchable-dropdown-icon" />
                    <input
                      type="text"
                      value={item.productSearch}
                      onChange={(e) => {
                        handleSearchChange(index, e.target.value);
                        queueFetch(item.id, e.target.value);
                      }}
                      onFocus={() => {
                        updateItem(index, { showDropdown: true });
                        queueFetch(item.id, item.productSearch);
                      }}
                      placeholder={
                        isLoading
                          ? t("se.stockRequest.loadingProducts", {
                              defaultValue: "Loading products...",
                            })
                          : t("se.stockRequest.searchProduct", {
                              defaultValue: "Search product by model",
                            })
                      }
                      className="searchable-dropdown-text-input"
                    />
                  </div>
                  {item.showDropdown && (
                    <div className="dropdown-list">
                      {isLoading ? (
                        <div className="dropdown-item disabled">
                          {t("se.stockRequest.loadingProducts", {
                            defaultValue: "Loading products...",
                          })}
                        </div>
                      ) : options.length > 0 ? (
                        options.map((variant) => (
                          <div
                            key={variant.product_id}
                            className="dropdown-item"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleSelect(index, variant);
                            }}
                          >
                            <div className="sale-model-option-name">
                              {getVariantLabel(variant)}
                            </div>
                            <div className="sale-model-option-meta">
                              {variant.product_model_description ||
                                variant.product_description ||
                                ""}
                            </div>
                            <div className="sr-option-stock">
                              {t("se.stockRequest.inWarehouseCount", {
                                count: variant.available_stock,
                                defaultValue: `${variant.available_stock} in warehouse`,
                              })}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item disabled">
                          {t("se.stockRequest.noProductsFound", {
                            defaultValue: "No products found",
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {meta && (
                  <div className="sale-model-meta">
                    <div>
                      <strong>
                        {t("se.stockRequest.categoryLabel", {
                          defaultValue: "Category:",
                        })}
                      </strong>{" "}
                      {meta.categoryName ||
                        t("se.stockRequest.notAvailable", { defaultValue: "N/A" })}
                    </div>
                    <div>
                      <strong>
                        {t("se.stockRequest.descriptionLabel", {
                          defaultValue: "Description:",
                        })}
                      </strong>{" "}
                      {meta.modelDescription ||
                        meta.productName ||
                        t("se.stockRequest.notAvailable", { defaultValue: "N/A" })}
                    </div>
                  </div>
                )}
              </div>

              {/* Warehouse stock is informational only — the promoter may
                  request more than this. */}
              {meta && (
                <div className="sr-warehouse-hint">
                  <Package size={16} className="sr-warehouse-hint-icon" />
                  <span className="sr-warehouse-hint-name">
                    {t("se.stockRequest.inStock", { defaultValue: "In warehouse" })}
                    : <strong>{meta.availableStock}</strong>
                  </span>
                </div>
              )}

              <div className="form-group sr-last-field">
                <label>
                  {t("se.stockRequest.quantityLabel", {
                    defaultValue: "Quantity requested",
                  })}
                  <span className="required-asterisk" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) =>
                    updateItem(index, { quantity: e.target.value })
                  }
                  placeholder="1"
                  required
                />
              </div>
            </div>
          );
        })}

        {items.length < MAX_ITEMS && (
          <button type="button" onClick={addItem} className="sale-add-item-btn">
            <span style={{ fontSize: "18px" }}>+</span>{" "}
            {t("se.stockRequest.addAnotherItem", {
              defaultValue: "Add another item",
            })}
          </button>
        )}

        <div className="sale-receipt-card">
          <div className="form-group sr-last-field">
            <label>
              {t("se.stockRequest.notesLabel", {
                defaultValue: "Notes (optional)",
              })}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("se.stockRequest.notesPlaceholder", {
                defaultValue: "Add any notes for the warehouse team",
              })}
              rows="2"
            />
          </div>
        </div>

        {formError && <div className="form-error">{formError}</div>}

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-cancel"
            disabled={submitting}
          >
            {t("se.stockRequest.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="submit" className="btn-submit" disabled={submitting}>
            {submitting
              ? t("se.stockRequest.submitting", { defaultValue: "Submitting..." })
              : t("se.stockRequest.submit", { defaultValue: "Submit Request" })}
          </button>
        </div>
      </form>
    </div>
  );
}

export default StockRequestForm;
